import { Stack } from 'component-library';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { useYAxisDomainFromMouseWheelZoom } from '@/components/Charts/hooks/useYAxisDomainFromMouseWheelZoom';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { ChartShape, CompositeGraph } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { printYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { NoData } from '../../../../components/NoData';

const lifeExpectancyColors = getColorForIndicator(INDICATOR.FREE_SOCIAL_LIFE_EXPECTANCY);

export const SocialLifeExpectancyGraphTile = (props?: GraphTileProps | null) => {
  const { t } = useTranslation();
  const plotGroupId = props?.plotGroupId ?? null;

  const { [R1FactType.social_life_expectancy_historic_graph]: socialHistoric } = useOrganizationIndicatorGraphFact<
    GraphFact[]
  >(R1FactType.social_life_expectancy_historic_graph, null, undefined, plotGroupId);

  const chartData = socialHistoric?.value;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, [0, 100]);

  if (!chartData?.length) {
    return <NoData />;
  }

  return (
    <Stack center data-testid='social-life-expectancy-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={props?.graphType}
          indicator={GraphTileType.social_life_expectancy}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          zoom={props?.zoom}
          setZoom={props?.setZoom}
          defaultGraphType={
            <CompositeGraph
              data={chartData}
              chartConfig={[
                {
                  shape: ChartShape.line,
                  id: 'social_life_expectancy',
                  key: 'social_life_expectancy',
                  dataKey: 'social_life_expectancy.value',
                  strokeWidth: 2,
                  label: t('shared.ncaDetail.details.social.labels.lifeExpectancy'),
                  color: lifeExpectancyColors.stroke,
                  unit: 'years',
                },
              ]}
              xAxisProps={{
                dataKey: 'label',
                tickFormatter: printYear,
                domain: ['dataMin', 'dataMax'],
              }}
              yAxisProps={{
                label: t('shared.ncaDetail.details.social.labels.years'),
                domain: yAxisDomainFromMouseWheel,
              }}
              className='relative w-full'
              legendItems={[
                {
                  label: t('shared.ncaDetail.details.social.labels.lifeExpectancy'),
                  color: lifeExpectancyColors.stroke,
                },
              ]}
              ref={chartContainerRef}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
